import { useEffect } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Skeleton from 'react-loading-skeleton';
import { useAccessToken } from '@hooks/useAccessToken';
import { useAuth0User } from '@hooks/useAuth0User';
import { useEntitlements } from '@hooks/useEntitlements';

import { useLeagueContext } from '@hooks/useLeagueContext';
import Layout from '@components/layout';
import { isPaidMember } from '@lib/utils';
import { buildHomepageJsonLd, getPageSeoHead, PAGE_SEO } from '@lib/page-seo';
import HomepageHero from '@components/homepage/homepage-hero';
import HowYouBeatYourMates from '@components/homepage/how-you-beat-your-mates';
import ThisGameweek from '@components/homepage/this-gameweek';
import TrustedByManagers from '@components/homepage/trusted-by-managers';
import HomepageFaq from '@components/homepage/homepage-faq';
import FeaturedArticle from '@components/featured-article';
import { LatestArticles } from '@components/homepage/latest-articles';
import {
  getHomepageSettings,
  getLatestListingCards,
  getMarketingCopy,
  getPredictedLineupsCard
} from '@lib/sanity';
import type { MarketingCopy } from '@lib/sanity';
import { reportIsrRegenerationFailure } from '@lib/isr/report-regeneration-failure';
import { fetchAppStoreRating } from '@lib/app-store-rating';
import type { AppStoreRating } from '@lib/app-store-rating';
import {
  HOMEPAGE_SOURCE_LIMIT,
  buildHomepageListing
} from '@lib/homepage/build';
import type { HomepageListing } from '@lib/homepage/build';
import { HOMEPAGE_FAQ } from '@lib/homepage/faq';
import { cardLabelFor } from '@lib/homepage/tabs';
import type { HomepageTab } from '@lib/homepage/tabs';
import { pickThisGameweekCards } from '@lib/homepage/this-gameweek';
import type { ThisGameweekCard } from '@lib/homepage/this-gameweek';
import {
  normaliseSanityLeadStory,
  normaliseSanityListingItem
} from '@lib/listings/normalise';
import { toPageProps } from '@lib/listings/page-props';

const REVALIDATE_SECONDS = 300;

// Paying members get the article homepage, but the static HTML is always the
// logged-out page so crawlers see it. A browser last seen as a paying member
// hides that page behind the skeleton until membership settles, as it did,
// instead of flashing the marketing page at a member. The hint is a
// convenience: a missing or wrong one only costs that flash or a skeleton.
const MEMBER_VIEW_STORAGE_KEY = 'ffh-homepage-member-view';
const MEMBER_VIEW_ATTRIBUTE = 'data-homepage-member-view';
const MEMBER_VIEW_HINT_SCRIPT = `try{if(localStorage.getItem('${MEMBER_VIEW_STORAGE_KEY}')==='1'){document.documentElement.setAttribute('${MEMBER_VIEW_ATTRIBUTE}','')}}catch(e){}`;

const rememberMemberView = (isPayingMember: boolean) => {
  try {
    if (isPayingMember) {
      localStorage.setItem(MEMBER_VIEW_STORAGE_KEY, '1');
    } else {
      localStorage.removeItem(MEMBER_VIEW_STORAGE_KEY);
    }
  } catch {
    // Storage can be blocked (private windows, strict settings); the hint is
    // only an optimisation.
  }
};

const clearMemberViewHint = () =>
  document.documentElement.removeAttribute(MEMBER_VIEW_ATTRIBUTE);

type HomeProps = HomepageListing & {
  heading: string;
  tabs: HomepageTab[];
  cardCount: number;
  marketingCopy: MarketingCopy;
  thisGameweekCards: ThisGameweekCard[];
  appStoreRating: AppStoreRating | null;
};

const Home = ({
  lead,
  items,
  heading,
  tabs,
  cardCount,
  marketingCopy,
  thisGameweekCards,
  appStoreRating
}: HomeProps) => {
  const { context } = useLeagueContext();
  const nextGameweek = context?.nextGameweek?.number ?? null;
  const { user, isLoading } = useAuth0User();
  const { accessTokenIsFetching } = useAccessToken();
  const { isEntitlementsLoading } = useEntitlements();
  const isPayingMember = !isLoading && isPaidMember(user);
  // Session claims can predate an upgrade, so a signed-in visitor's membership
  // is only settled once the entitlements read behind them has finished.
  const isMembershipSettling =
    isLoading ||
    (Boolean(user) && (accessTokenIsFetching || isEntitlementsLoading));

  useEffect(() => {
    if (isMembershipSettling) return;
    rememberMemberView(isPayingMember);
    if (!isPayingMember) clearMemberViewHint();
  }, [isMembershipSettling, isPayingMember]);

  useEffect(() => clearMemberViewHint, []);

  const router = useRouter();
  const pageHead = [
    ...getPageSeoHead(router.pathname, PAGE_SEO.home),
    <script
      type="application/ld+json"
      key="homepage-jsonld"
      dangerouslySetInnerHTML={{
        __html: buildHomepageJsonLd({ faq: HOMEPAGE_FAQ, appStoreRating })
      }}
    />
  ];

  if (isPayingMember) {
    return (
      <>
        <Head>{pageHead}</Head>
        <FeaturedArticle
          nextGameweek={nextGameweek}
          item={lead}
          label={lead ? cardLabelFor(lead, tabs) : undefined}
        />
        <Layout hasRightSidebar content={false}>
          <LatestArticles
            heading={heading}
            items={items}
            tabs={tabs}
            cardCount={cardCount}
          />
        </Layout>
      </>
    );
  }

  return (
    <>
      <Head>{pageHead}</Head>
      {isMembershipSettling && (
        <>
          <script
            dangerouslySetInnerHTML={{ __html: MEMBER_VIEW_HINT_SCRIPT }}
          />
          <div
            aria-hidden="true"
            className="hidden h-[95vh] py-10 [[data-homepage-member-view]_&]:block"
          >
            <Skeleton count={12} width={'100%'} />
          </div>
        </>
      )}
      <main className="[[data-homepage-member-view]_&]:hidden">
        <HomepageHero
          copy={marketingCopy.homepage}
          appStoreRating={appStoreRating}
        />
        <HowYouBeatYourMates />
        <ThisGameweek cards={thisGameweekCards} />
        <TrustedByManagers />
        <HomepageFaq entries={HOMEPAGE_FAQ} />
      </main>
    </>
  );
};

export const getStaticProps = async () => {
  try {
    const [
      sanityCards,
      settings,
      marketingCopy,
      predictedLineupsCard,
      appStoreRating
    ] = await Promise.all([
      getLatestListingCards(HOMEPAGE_SOURCE_LIMIT),
      getHomepageSettings(),
      getMarketingCopy(),
      getPredictedLineupsCard(),
      fetchAppStoreRating()
    ]);

    const listing = buildHomepageListing({
      sanityCards,
      leadStory: settings.leadStory
    });

    // From the raw newest-first list rather than the listing, which moves a
    // pinned lead story out of date order.
    const thisGameweekCards = pickThisGameweekCards({
      items: sanityCards.map(normaliseSanityListingItem),
      tabs: settings.tabs,
      predictedLineups: predictedLineupsCard
        ? normaliseSanityLeadStory(predictedLineupsCard)
        : null
    });

    return {
      props: {
        ...listing,
        heading: settings.heading,
        tabs: settings.tabs,
        cardCount: settings.cardCount,
        marketingCopy,
        thisGameweekCards: toPageProps(thisGameweekCards),
        appStoreRating
      },
      revalidate: REVALIDATE_SECONDS
    };
  } catch (error) {
    return reportIsrRegenerationFailure('/', error);
  }
};

export default Home;
